import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { archiveDevice, unarchiveDevice } from '@/services/devices';
import { useDeviceStore } from '@/stores/useDeviceStore';
import { LOG_PREFIX } from '@/config/constants';
import { Archive, ArchiveRestore } from 'lucide-react';
import type { Device } from '@/types';

interface ArchiveDeviceDialogProps {
  device: Device;
}

export function ArchiveDeviceDialog({ device }: ArchiveDeviceDialogProps) {
  const [open, setOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const updateDevice = useDeviceStore((s) => s.updateDevice);
  const selectDevice = useDeviceStore((s) => s.selectDevice);

  const isArchived = device.archived_at != null;

  async function handleSubmit() {
    setIsPending(true);
    setErrorMessage(null);
    try {
      if (isArchived) {
        await unarchiveDevice(device.id);
        updateDevice(device.id, { archived_at: null });
      } else {
        const archivedAt = await archiveDevice(device.id);
        updateDevice(device.id, { archived_at: archivedAt });
      }
      setOpen(false);
      selectDevice(null);
    } catch (err) {
      console.error(LOG_PREFIX, 'Failed to update archive state:', err);
      setErrorMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setIsPending(false);
    }
  }

  const label = isArchived ? 'Unarchive' : 'Archive';
  const Icon = isArchived ? ArchiveRestore : Archive;
  const pendingLabel = isArchived ? 'Unarchiving...' : 'Archiving...';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setErrorMessage(null);
      }}
    >
      <DialogTrigger>
        <button
          className="rounded p-1 text-gray-400 hover:bg-white hover:text-brand-purple"
          aria-label={`${label} device`}
          title={`${label} device`}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{label} Device</DialogTitle>
          <DialogDescription>
            {isArchived ? (
              <>
                Restore <strong>{device.device_name}</strong> to the active
                device list. It will resume appearing in the default view.
              </>
            ) : (
              <>
                Hide <strong>{device.device_name}</strong> from the default
                device list. Its location history is preserved and you can
                unarchive it at any time.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {errorMessage && (
          <div className="rounded border border-brand-orange bg-brand-orange-light px-3 py-2 text-sm text-brand-orange">
            {errorMessage}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? pendingLabel : label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
